/**
 * =============================================================================
 *  MOT BO DIEU KHIEN KIEU CHU, DUNG CHO CA HAI CHO SOAN CHU
 * =============================================================================
 *
 *  Ung dung nay co HAI cho nguoi dung go chu:
 *    (1) hop chu tu do / ghi chu  -> thanh thuoc tinh (PropertyBar)
 *    (2) chu SAN CO trong tai lieu -> trinh soan tai cho (InlineTextEditor)
 *
 *  Ve mat nguoi dung do la CUNG MOT viec: chon dam, nghieng, can le. Truoc day
 *  hai cho co hai bo nut rieng, hai bang phim tat rieng, hai kieu nhan rieng -
 *  va chung da bat dau troi khoi nhau (mot ben goi la "Căn giữa", ben kia hien
 *  mot bieu tuong khong nhan; mot ben doi nac ngay, ben kia doi sau mot nhip).
 *
 *  Nen cac nut do song O DAY, mot lan. Cai gi hai cho khac nhau THAT thi khac
 *  nhau MOT CACH NHIN THAY DUOC chu khong khac ve cau truc:
 *
 *    - Chu san co co CHAN DOAN FONT, co the bi TU CHOI vi thieu glyph, va co
 *      CANH BAO THAY FONT. Hop chu moi thi khong - no luon duoc ve bang font
 *      dong goi nen khong co gi de canh bao.
 *    - Chu san co co thao tac CHE (che chu cu bang mot mang mau nen). Hop chu
 *      moi thi thao tac tuong ung la XOA THAT, vi doi tuong do do chinh ta tao
 *      ra va go no di la go han.
 *
 *  Hai khac biet do duoc the hien bang cac thanh phan RIENG dat canh bo nut nay,
 *  chu khong bang mot bo nut thu hai.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AlignIcon } from './AlignIcon';
import {
  ALIGN_LABEL,
  ALIGN_SHORTCUT,
  FONT_SIZE_MAX_PT,
  FONT_SIZE_MIN_PT,
  formatFontSizePt,
  parseFontSizePt,
  type TextAlign,
} from './textEditCore';
import {
  FONT_FAMILIES,
  normalizeFontFamilyId,
  type FontFamilyId,
} from '../core/fonts/families';

export const ALIGN_ORDER: readonly TextAlign[] = ['left', 'center', 'right'];

/**
 * O CHON PHONG CHU, DUNG CHUNG CHO CA HAI DUONG SOAN CHU.
 *
 * =============================================================================
 *  VI SAO KHONG CON MOT O BI KHOA
 * =============================================================================
 *  Truoc day ung dung chi dong goi mot ho font phan phoi lai hop phap (Noto Sans),
 *  nen o chon phong bi KHOA cung, kem mot loi giai thich. Gio co BA ho font, ca
 *  ba deu OFL-1.1 (cho phep nhung VA phan phoi lai), da kiem du glyph cho toan bo
 *  bang chu cai tieng Viet. Nen o day mo khoa thanh mot <select> that.
 *
 *  DANH SACH LAY THANG TU SO DANG KY core/fonts/families.ts, khong go cung o day.
 *  Mot ban thu hai cua danh sach la cach o chon (duong xem) va duong nhung font
 *  (duong ghi) bat dau tra loi khac nhau cho cung mot cau hoi "co nhung phong
 *  nao" - dung bai hoc so 3 o quy mo danh sach.
 *
 *  Ma tra ve luon di qua normalizeFontFamilyId: mot gia tri khong doc ra (du lieu
 *  cu, loi tuan tu hoa) ve phong mac dinh chu khong lam trong o chon.
 *
 *  O NAY SONG O DAY, MOT LAN. Ca thanh thuoc tinh (PropertyBar, cho hop chu) lan
 *  vo soan chu (TextEditorShell, cho ca sua chu san co lan hop chu moi) deu dung
 *  chinh no - chep no ra lam ban thu hai la cach hai duong soan chu troi khoi nhau.
 */
export function FontFamilySelect({
  value,
  onChange,
  disabled = false,
}: {
  /** Ma ho font hien co, hoac undefined = phong mac dinh. */
  value: string | undefined;
  onChange: (id: FontFamilyId) => void;
  disabled?: boolean;
}) {
  const current = normalizeFontFamilyId(value);
  return (
    <select
      className="propSelect"
      value={current}
      disabled={disabled}
      aria-label="Phông chữ"
      title="Phông chữ nhúng vào tệp. Cả ba đều theo giấy phép OFL-1.1 — được phép nhúng và phân phối lại. Chỉ mặt chữ thật sự dùng mới được nhúng vào bản xuất."
      onChange={(e) => onChange(normalizeFontFamilyId(e.target.value))}
    >
      {FONT_FAMILIES.map((f) => (
        <option key={f.id} value={f.id}>
          {f.label}
        </option>
      ))}
    </select>
  );
}

/** Cac buoc co chu quen thuoc. Nguoi dung van go duoc so bat ky. */
const SIZE_STEPS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48];

/**
 * O GHEP CO CHU: go tu do + danh sach nac co san.
 *
 * O nay tung song trong PropertyBar.tsx. No chuyen ve day khi vo soan chu duoc
 * gop lam mot: hop chu moi phai dat duoc co chu NGAY TRONG LUC GO chu khong
 * phai luu xong roi voi tay sang thanh thuoc tinh. Chep no ra lam ban thu hai
 * la dung cach ma hai duong soan chu nay da troi khoi nhau lan truoc.
 *
 * VI SAO KHONG DUNG <input list> VOI <datalist>. Ve hop dong HTML thi datalist
 * dung y het thu ta can, va no la lua chon dau tien duoc thu. Van de la CHO
 * BAM: Chrome khong ve nut mo danh sach co dinh - mui ten chi hien khi con tro
 * di ngang qua o. Tren mot o rong 58px, mot cho bam chi ton tai luc di chuot
 * ngang qua thi khong ai tim ra. Dung nac co san la nua cong dung cua o nay,
 * nen cho bam phai NHIN THAY DUOC. Vi vay dung o ghep tu ve.
 *
 * Duong ghi luon di qua parseFontSizePt: chuoi rong, chu, hay so ngoai khoang
 * 4-200pt deu KHONG ghi gi ca. Khi roi con tro ma chuoi van khong doc duoc, o
 * quay ve dung co chu that cua doi tuong - khong bao gio de lai mot o hien mot
 * dang ma doi tuong mang mot neo.
 */
export function FontSizeCombo({
  value,
  onCommit,
}: {
  value: number;
  onCommit: (pt: number) => void;
}) {
  const [draft, setDraft] = useState(() => formatFontSizePt(value));
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  // Co chu doi tu NOI KHAC (chon doi tuong khac, Ctrl+Z, keo o soan): o phai
  // theo. Neu khong, undo se de lai con so cu tren man hinh.
  //
  // NHUNG chi khi chuoi dang go KHONG doc ra dung con so ay. Viet thang
  // setDraft(formatFontSizePt(value)) thi go so thap phan thanh KHONG THE: go
  // "10." -> Number("10.") la 10 -> ghi 10 -> effect nay chay -> o bi viet lai
  // thanh "10" -> dau cham vua go bien mat, va nguoi dung khong bao gio den
  // duoc "10.5". Da bat duoc dung loi nay khi chay thu tren trinh duyet.
  useEffect(() => {
    setDraft((d) => (parseFontSizePt(d) === value ? d : formatFontSizePt(value)));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const revert = () => setDraft(formatFontSizePt(value));

  const typed = (raw: string) => {
    setDraft(raw);
    const pt = parseFontSizePt(raw);
    if (pt !== null) onCommit(pt);
  };

  const pick = (pt: number) => {
    setDraft(formatFontSizePt(pt));
    onCommit(pt);
    setOpen(false);
  };

  // Mui ten len/xuong tang giam mot diem - hanh vi cua <input type="number"> cu.
  // Bo no di la lay mat mot thu nguoi dung dang co.
  const bump = (delta: number) => {
    const base = parseFontSizePt(draft) ?? value;
    const next = Math.max(FONT_SIZE_MIN_PT, Math.min(FONT_SIZE_MAX_PT, base + delta));
    setDraft(formatFontSizePt(next));
    onCommit(next);
  };

  return (
    <div className="propCombo" ref={rootRef}>
      <input
        className="propComboInput"
        type="text"
        inputMode="decimal"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        autoComplete="off"
        value={draft}
        aria-label="Cỡ chữ"
        title={`Cỡ chữ, tính bằng điểm (${FONT_SIZE_MIN_PT}–${FONT_SIZE_MAX_PT}). Gõ số bất kỳ hoặc bấm mũi tên để chọn cỡ có sẵn.`}
        onChange={(e) => typed(e.target.value)}
        onBlur={() => {
          if (parseFontSizePt(draft) === null) revert();
        }}
        onKeyDown={(e) => {
          // O nay nam TRONG vo soan chu, va vo soan coi Enter la "xac nhan".
          // Chan su kien lai o day: go xong co chu roi bam Enter phai la "chot
          // con so nay", khong phai "luu ca hop chu roi dong o soan".
          e.stopPropagation();
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            bump(1);
          } else if (e.key === 'ArrowDown' && e.altKey) {
            e.preventDefault();
            setOpen(true);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            bump(-1);
          } else if (e.key === 'Escape') {
            if (open) setOpen(false);
            else revert();
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (parseFontSizePt(draft) === null) revert();
            setOpen(false);
          }
        }}
      />
      <button
        type="button"
        className="propComboArrow"
        tabIndex={-1}
        aria-label="Cỡ chữ có sẵn"
        aria-expanded={open}
        title="Cỡ chữ có sẵn"
        onClick={() => setOpen((o) => !o)}
      >
        <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true" focusable="false">
          <path d="M1 3.5 L5 7 L9 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>
      {open && (
        <ul className="propComboList" id={listId} role="listbox" aria-label="Cỡ chữ có sẵn">
          {SIZE_STEPS.map((s) => (
            <li key={s}>
              <button
                type="button"
                role="option"
                aria-selected={Math.abs(s - value) < 0.05}
                className={`propComboItem${Math.abs(s - value) < 0.05 ? ' active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * DAM / NGHIENG.
 *
 * Nhan "B" va "I" chu khong phai chu "Đậm"/"Nghiêng": hai o vuong nho nam vua
 * duoc ca tren thanh thuoc tinh lan trong bang phan hoi chat hep cua trinh soan
 * tai cho. Nghia cua chung nam o `title` va o `aria-label`.
 */
export function StyleToggles({
  bold,
  italic,
  onBold,
  onItalic,
  disabled = false,
  boldTitle = 'Đậm — dùng bản Noto Sans Bold',
  italicTitle = 'Nghiêng — dùng bản Noto Sans Italic',
}: {
  bold: boolean;
  italic: boolean;
  onBold: (next: boolean) => void;
  onItalic: (next: boolean) => void;
  disabled?: boolean;
  boldTitle?: string;
  italicTitle?: string;
}) {
  return (
    <div className="propGroup" role="group" aria-label="Kiểu chữ">
      <button
        type="button"
        className={`propToggle bold${bold ? ' active' : ''}`}
        aria-pressed={bold}
        aria-label="Đậm"
        disabled={disabled}
        title={boldTitle}
        onClick={() => onBold(!bold)}
      >
        B
      </button>
      <button
        type="button"
        className={`propToggle italic${italic ? ' active' : ''}`}
        aria-pressed={italic}
        aria-label="Nghiêng"
        disabled={disabled}
        title={italicTitle}
        onClick={() => onItalic(!italic)}
      >
        I
      </button>
    </div>
  );
}

/**
 * CAN LE.
 *
 * `active` co the la null, va do la mot trang thai THAT chu khong phai thieu
 * du lieu: voi chu san co, khi loi chua kip tra bao cao ve thi ta CHUA BIET can
 * le nao dang co hieu luc. Khong nut nao sang con hon mot nut sang sai roi nhay
 * sang nut khac mot nhip sau.
 */
export function AlignToggles({
  active,
  onChange,
  disabled = false,
  disabledTitle,
}: {
  active: TextAlign | null;
  onChange: (align: TextAlign) => void;
  disabled?: boolean;
  disabledTitle?: string;
}) {
  return (
    <div className="propGroup" role="group" aria-label="Căn lề">
      {ALIGN_ORDER.map((a) => (
        <button
          key={a}
          type="button"
          className={`propToggle icon${active === a ? ' active' : ''}`}
          aria-pressed={active === a}
          aria-label={ALIGN_LABEL[a]}
          disabled={disabled}
          title={disabled && disabledTitle ? disabledTitle : `${ALIGN_LABEL[a]} (${ALIGN_SHORTCUT[a]})`}
          onClick={() => {
            if (active !== a) onChange(a);
          }}
        >
          <AlignIcon align={a} />
        </button>
      ))}
    </div>
  );
}

/**
 * THAO TAC PHA HUY - va cai nhan cua no la phan quan trong nhat cua ca tep nay.
 *
 * `label` do ben goi dua vao, KHONG co gia tri mac dinh, va do la co y. Hai cho
 * goi ham nay lam hai viec khac han nhau:
 *
 *    hop chu moi  -> "Xóa"    : doi tuong bien mat that.
 *    chu san co   -> "Che chữ": mot mang mau nen phu len tren. Chu goc VAN NAM
 *                               NGUYEN trong tep, van sao chep va tim kiem ra
 *                               duoc.
 *
 * Neu co mot nhan mac dinh, cho nao quen dua nhan vao se lang le hien "Xóa" cho
 * mot thao tac chi che. Mot nguoi dung che con so trong bao gia roi gui di, tin
 * rang no da bien mat, la ket qua truc tiep cua dung mot cai nhan dat sai.
 */
export function DestructiveAction({
  label,
  title,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  /** Ghi chu hien ngay duoi nut. Bat buoc voi thao tac chi CHE. */
  children?: ReactNode;
}) {
  return (
    <div className="destructiveAction">
      <button
        type="button"
        className="button small danger"
        disabled={disabled}
        title={title}
        onClick={onClick}
      >
        {label}
      </button>
      {children}
    </div>
  );
}
